import axios from 'axios'
import { fetchAuthSession } from 'aws-amplify/auth'
import type { AttachedExperienceReference } from '@/types/form.types'

const API_URL = import.meta.env.DEV ? '/api' : import.meta.env.VITE_API_URL

export type ManagedCampaign = {
  id: string
  name: string
  description?: string | null
  startDate?: string | null
  endDate?: string | null
  timezone?: string | null
  createdAt: string
  updatedAt?: string | null
  paused?: boolean
  enabled?: boolean
  status?: string | null
  subDomain?: string | null
  path?: string | null
  orgId?: string | null
  tenantId?: string | null
  logo?: string | null
  favicon?: string | null
  googleFont?: string | null
  pageTitle?: string | null
  prizeMessage?: string | null
  primaryColor?: string | null
  secondaryColor?: string | null
  backgroundColor?: string | null
  headerColor?: string | null
  buttonTextColor?: string | null
  buttonConfigs?: Record<string, unknown> | null
  screenAppearances?: Record<string, unknown> | null
  tier?: string | null
  // Type of experience attached to this campaign — drives UI gates such
  // as the "View Responses" dashboard action (shown for 'survey' + 'quiz').
  experienceType?: string | null
  // Boundary axis (orthogonal to experienceType) — drives analytics gates
  // such as hiding the Geolocation chart for instant campaigns. Null for
  // legacy campaigns created before this field was persisted server-side.
  boundary?: 'instant' | 'geo' | null
}

export type ManagedCampaignUpdate = {
  name?: string
  description?: string
  startDate?: string | null
  endDate?: string | null
  timezone?: string | null
  paused?: boolean
}

export type CapStatus = {
  activeCount: number
  limit: number
  overBy: number
}


export type ManagedExperienceSession = {
  sessionId: string
  campaignId: string
  userId: string
  walletId: string
  memberId?: string | null
  externalCustomerId?: string | null
  billingSessionId?: string | null
  journeyId?: string | null
  status: string
  experienceType: string
  slotId?: string
  roundId?: string
  stageId?: string
  stageOrder?: number
  startedAt: string
  completedAt?: string | null
  totalStages: number
  totalQuestions?: number
  score: number
}

export type ManagedExperienceSessionDetail = {
  session: ManagedExperienceSession
  stages: Array<Record<string, unknown>>
  outbox: ManagedLoyaltyOutboxRecord[]
  fulfilments: ManagedLoyaltyFulfilment[]
}

export type ManagedLoyaltyOutboxRecord = {
  campaignId: string
  userId: string
  sessionId: string
  eventId: string
  eventName: string
  eventKey: string
  idempotencyKey: string
  status: string
  detail: Record<string, unknown>
  dispatchAttempts?: number
  lastError?: string
  createdAt: string
  updatedAt?: string
  dispatchedAt?: string
}

export type ManagedLoyaltyFulfilment = {
  fulfilmentId: string
  userId: string
  eventName: string
  memberId?: string | null
  externalCustomerId?: string | null
  status: string
  processingNote?: string
  execution?: Record<string, unknown>
  createdAt: string
  processedAt?: string
}

export type ManagedCampaignEditDraft = {
  campaign: ManagedCampaign & Record<string, unknown>
  questionPack: Record<string, unknown> | null
  attachedExperience?: AttachedExperienceReference | null
}

export type PrizeBudgetStatus = {
  currentBucket: {
    bucketStart: string
    bucketEnd: string
    cap: number
    wins: number
    remaining: number
  }
  round: {
    roundId: string
    totalPrizes: number
    alreadyWonInRound: number
    prizesRemaining: number
    bucketsRemaining: number
  }
  recentBuckets: Array<{
    bucketStart: string
    cap: number
    wins: number
  }>
}

export type ManagedCampaignUser = {
  id: string
  campaignId: string
  externalId?: string | null
  email?: string | null
  firstName?: string | null
  lastName?: string | null
  mobileNumber?: string | null
  createdAt?: string | null
  updatedAt?: string | null
}

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const session = await fetchAuthSession()
  const token = session.tokens?.idToken?.toString() || session.tokens?.accessToken?.toString()
  if (!token) {
    throw new Error('You must be logged in to view campaigns.')
  }
  return { Authorization: `Bearer ${token}` }
}

export const campaignsService = {
  async listCampaigns(limit = 100): Promise<ManagedCampaign[]> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: ManagedCampaign[] }>(
      `${API_URL}/manager/campaigns`,
      {
        headers,
        params: { limit },
      }
    )
    return response.data.data || []
  },

  async getCampaign(campaignId: string): Promise<ManagedCampaign> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: ManagedCampaign }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}`,
      { headers }
    )
    return response.data.data
  },

  async listCampaignUsers(campaignId: string, limit = 1000, lastKey?: string): Promise<{ items: ManagedCampaignUser[]; lastKey: string | null }> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: { items: ManagedCampaignUser[]; lastKey: string | null } }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/users`,
      {
        headers,
        params: {
          limit,
          lastKey,
        },
      }
    )
    return response.data.data || { items: [], lastKey: null }
  },

  async getCampaignEditDraft(campaignId: string): Promise<ManagedCampaignEditDraft> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: ManagedCampaignEditDraft }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/edit-draft`,
      { headers }
    )
    return response.data.data
  },

  async updateCampaign(campaignId: string, payload: ManagedCampaignUpdate): Promise<ManagedCampaign> {
    const headers = await getAuthHeaders()
    const response = await axios.put<{ success: boolean; data: ManagedCampaign }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}`,
      payload,
      { headers }
    )
    return response.data.data
  },

  /**
   * Package H — directly attach (or detach) a published experience to a campaign
   * without running the full customisation wizard. Updates only the
   * `attachedExperience` field in the campaign's S3 question pack.
   * Backend: PATCH /manager/campaigns/{campaignId}/attach-experience
   */
  async attachExperience(
    campaignId: string,
    attachedExperience: AttachedExperienceReference | null,
  ): Promise<void> {
    const headers = await getAuthHeaders()
    await axios.patch(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/attach-experience`,
      { attachedExperience },
      { headers },
    )
  },

  async pauseCampaign(campaignId: string, paused: boolean): Promise<ManagedCampaign> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: ManagedCampaign }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/pause`,
      { paused },
      { headers }
    )
    return response.data.data
  },

  async activateCampaign(campaignId: string): Promise<ManagedCampaign> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: ManagedCampaign }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/activate`,
      {},
      { headers }
    )
    return response.data.data
  },

  async deactivateCampaign(campaignId: string): Promise<ManagedCampaign> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: ManagedCampaign }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/deactivate`,
      {},
      { headers }
    )
    return response.data.data
  },

  async swapActiveCampaign(activateId: string, deactivateId: string): Promise<{ activated: ManagedCampaign; deactivated: ManagedCampaign }> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: { activated: ManagedCampaign; deactivated: ManagedCampaign } }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(activateId)}/swap-active`,
      { deactivateId },
      { headers }
    )
    return response.data.data
  },


  async recustomizeCampaign(campaignId: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const headers = await getAuthHeaders()
    const response = await axios.put<{ success: boolean; data: Record<string, unknown> }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/recustomize`,
      payload,
      { headers }
    )
    return response.data.data || {}
  },

  async listExperienceSessions(campaignId: string, params?: { userId?: string; memberId?: string; externalCustomerId?: string; status?: string }): Promise<ManagedExperienceSession[]> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: ManagedExperienceSession[] }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/experience/sessions`,
      { headers, params }
    )
    return response.data.data || []
  },

  async getExperienceSessionDetail(campaignId: string, sessionId: string): Promise<ManagedExperienceSessionDetail> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: ManagedExperienceSessionDetail }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/experience/sessions/${encodeURIComponent(sessionId)}`,
      { headers }
    )
    return response.data.data
  },

  async listLoyaltyFulfilments(campaignId: string, params?: { userId?: string; memberId?: string; externalCustomerId?: string; status?: string; eventName?: string }): Promise<ManagedLoyaltyFulfilment[]> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: ManagedLoyaltyFulfilment[] }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/loyalty/fulfilments`,
      { headers, params }
    )
    return response.data.data || []
  },

  async listLoyaltyOutbox(campaignId: string, params?: { sessionId?: string; userId?: string; status?: string; eventName?: string }): Promise<ManagedLoyaltyOutboxRecord[]> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: ManagedLoyaltyOutboxRecord[] }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/loyalty/outbox`,
      { headers, params }
    )
    return response.data.data || []
  },

  async processLoyaltyOutbox(campaignId: string, limit = 25): Promise<Record<string, unknown>> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: Record<string, unknown> }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/loyalty/outbox/process`,
      { limit },
      { headers }
    )
    return response.data.data || {}
  },

  async redriveLoyaltyOutbox(campaignId: string, payload: {
    userId: string
    sessionId: string
    eventName: string
    eventKey?: string
  }): Promise<ManagedLoyaltyOutboxRecord> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: ManagedLoyaltyOutboxRecord }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/loyalty/outbox/redrive`,
      payload,
      { headers }
    )
    return response.data.data
  },

  async processLoyaltyFulfilment(campaignId: string, fulfilmentId: string, userId: string): Promise<ManagedLoyaltyFulfilment> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: ManagedLoyaltyFulfilment }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/loyalty/fulfilments/${encodeURIComponent(fulfilmentId)}/process`,
      { userId },
      { headers }
    )
    return response.data.data
  },

  async processLoyaltyFulfilments(campaignId: string, params?: { userId?: string; memberId?: string; externalCustomerId?: string; status?: string; eventName?: string; limit?: number }): Promise<Record<string, unknown>> {
    const headers = await getAuthHeaders()
    const response = await axios.post<{ success: boolean; data: Record<string, unknown> }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/loyalty/fulfilments/process`,
      params || {},
      { headers }
    )
    return response.data.data || {}
  },

  async getCapStatus(): Promise<CapStatus> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: CapStatus }>(
      `${API_URL}/manager/org/cap-status`,
      { headers }
    )
    return response.data.data
  },

  async getPrizeBudgetStatus(campaignId: string, roundId: string): Promise<PrizeBudgetStatus> {
    const headers = await getAuthHeaders()
    const response = await axios.get<{ success: boolean; data: PrizeBudgetStatus }>(
      `${API_URL}/manager/campaigns/${encodeURIComponent(campaignId)}/prize-budget/status`,
      { headers, params: { roundId } },
    )
    return response.data.data
  },
}
